import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, ScrollView } from "react-native";
import "../../theme";
import { useUnistyles } from "react-native-unistyles";
import { Icon } from "../Icon";
import { View } from "../View";
import { collectDefaultExpandedItemIds, styles } from "./ItemContent";
import { NavSidePanelSection } from "./Section";
import type { NavSidePanelProps } from "./NavSidePanel.types";

const DEFAULT_PANEL_WIDTH = 320;
const DEFAULT_MINIMIZED_PANEL_WIDTH = 84;
const DEFAULT_DEPTH_INDENT = 20;

export function NavSidePanel({
  header,
  items,
  footerItems = [],
  footer,
  width = DEFAULT_PANEL_WIDTH,
  minimizedWidth = DEFAULT_MINIMIZED_PANEL_WIDTH,
  minimizable = false,
  minimized,
  defaultMinimized = false,
  onMinimizedChange,
  scrollable = true,
  depthIndent = DEFAULT_DEPTH_INDENT,
  expandedItemIds,
  defaultExpandedItemIds,
  onExpandedItemIdsChange,
  onItemPress,
  renderHeader,
  renderFooter,
  renderItem,
  renderItemContent,
  renderDisclosureIcon,
  renderMinimizeButton,
  style,
  itemsContainerStyle,
  footerItemsContainerStyle,
  testID,
}: NavSidePanelProps) {
  const { theme } = useUnistyles();

  const initialExpandedIds = defaultExpandedItemIds ?? [
    ...collectDefaultExpandedItemIds(items),
    ...collectDefaultExpandedItemIds(footerItems),
  ];
  const [uncontrolledExpandedItemIds, setUncontrolledExpandedItemIds] = useState<readonly string[]>(initialExpandedIds);
  const [uncontrolledMinimized, setUncontrolledMinimized] = useState(defaultMinimized);
  const effectiveExpandedItemIds = expandedItemIds ?? uncontrolledExpandedItemIds;
  const effectiveMinimized = minimized ?? uncontrolledMinimized;
  const isExpandedControlled = expandedItemIds !== undefined;
  const isMinimizedControlled = minimized !== undefined;

  // Items often arrive asynchronously (e.g. after a data fetch), so the
  // defaultExpanded ids present at first render can miss ones that only show
  // up once real data replaces a loading/empty placeholder. Track which
  // default-expanded ids we've already applied so newly-appeared ones open
  // automatically without re-opening ids the user has since collapsed.
  const appliedDefaultExpandedIdsRef = useRef<Set<string>>(new Set(initialExpandedIds));
  useEffect(() => {
    // A caller-supplied defaultExpandedItemIds means they manage defaults explicitly.
    if (defaultExpandedItemIds !== undefined) return;
    const currentDefaultIds = [...collectDefaultExpandedItemIds(items), ...collectDefaultExpandedItemIds(footerItems)];
    const newIds = currentDefaultIds.filter((id) => !appliedDefaultExpandedIdsRef.current.has(id));
    if (newIds.length === 0) return;
    newIds.forEach((id) => appliedDefaultExpandedIdsRef.current.add(id));
    const idsToAdd = newIds.filter((id) => !effectiveExpandedItemIds.includes(id));
    if (idsToAdd.length === 0) return;
    const nextExpandedIds = [...effectiveExpandedItemIds, ...idsToAdd];
    if (!isExpandedControlled) {
      setUncontrolledExpandedItemIds(nextExpandedIds);
    }
    onExpandedItemIdsChange?.(nextExpandedIds);
  }, [
    items,
    footerItems,
    isExpandedControlled,
    defaultExpandedItemIds,
    effectiveExpandedItemIds,
    onExpandedItemIdsChange,
  ]);

  const handleToggleExpanded = useCallback(
    (itemId: string) => {
      const nextExpandedIds =
        effectiveExpandedItemIds.includes(itemId) ?
          effectiveExpandedItemIds.filter((expandedItemId) => expandedItemId !== itemId)
        : [...effectiveExpandedItemIds, itemId];

      if (!isExpandedControlled) {
        setUncontrolledExpandedItemIds(nextExpandedIds);
      }
      onExpandedItemIdsChange?.(nextExpandedIds);
    },
    [effectiveExpandedItemIds, isExpandedControlled, onExpandedItemIdsChange],
  );

  const handleToggleMinimized = useCallback(() => {
    const nextMinimized = !effectiveMinimized;
    if (!isMinimizedControlled) {
      setUncontrolledMinimized(nextMinimized);
    }
    onMinimizedChange?.(nextMinimized);
  }, [effectiveMinimized, isMinimizedControlled, onMinimizedChange]);

  const minimizeButton =
    minimizable || minimized !== undefined ?
      renderMinimizeButton ?
        renderMinimizeButton({ minimized: effectiveMinimized, onToggleMinimized: handleToggleMinimized })
      : <Pressable
          accessibilityRole="button"
          accessibilityLabel={effectiveMinimized ? "Expand navigation" : "Minimize navigation"}
          onPress={handleToggleMinimized}
          style={styles.minimizeButton}
        >
          <Icon name={effectiveMinimized ? "chevronRight" : "chevronLeft"} size={18} color={theme.colors.icon} />
        </Pressable>
    : null;

  const sectionProps = {
    depthIndent,
    expandedItemIds: effectiveExpandedItemIds,
    minimized: effectiveMinimized,
    onToggleExpanded: handleToggleExpanded,
    ...(onItemPress ? { onItemPress } : {}),
    ...(renderItem ? { renderItem } : {}),
    ...(renderItemContent ? { renderItemContent } : {}),
    ...(renderDisclosureIcon ? { renderDisclosureIcon } : {}),
  };

  const mainItems = <NavSidePanelSection items={items} style={itemsContainerStyle} {...sectionProps} />;

  return (
    <View
      width={effectiveMinimized ? minimizedWidth : width}
      bg="surfaceCard"
      borderWidth={1}
      borderColor="borderCard"
      p={effectiveMinimized ? "xl" : "2xl"}
      style={[styles.panel, style]}
      testID={testID}
    >
      {(header !== undefined || minimizeButton !== null) && (
        <View style={styles.header}>
          {renderHeader ?
            renderHeader({
              header,
              minimized: effectiveMinimized,
              minimizeButton,
              onToggleMinimized: handleToggleMinimized,
            })
          : <View
              flexDirection={effectiveMinimized ? "column" : "row"}
              alignItems="center"
              justifyContent="space-between"
              gap="md"
            >
              {!effectiveMinimized && header}
              {minimizeButton}
            </View>
          }
        </View>
      )}

      <View flex={1} style={styles.itemsRegion}>
        {scrollable ?
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
            {mainItems}
          </ScrollView>
        : mainItems}
      </View>

      {(footerItems.length > 0 || footer !== undefined) && (
        <View style={styles.footer}>
          {footer !== undefined && (!effectiveMinimized || renderFooter) && (
            <View style={styles.footerSlot}>
              {renderFooter ? renderFooter({ footer, minimized: effectiveMinimized }) : footer}
            </View>
          )}
          {footerItems.length > 0 && (
            <NavSidePanelSection items={footerItems} style={footerItemsContainerStyle} {...sectionProps} />
          )}
        </View>
      )}
    </View>
  );
}
